import React, { useImperativeHandle, forwardRef, useState, useContext, createContext } from "react";
import clsx from "clsx";

// Contexto para internacionalização (i18n) da placa
export const LicensePlateI18nContext = createContext<{ country?: string }>({ country: "BRASIL" });

export type PlateType =
  | "particular"
  | "comercial"
  | "oficial"
  | "diplomatico"
  | "preta"
  | "verde"
  | "oficial-antiga"
  | "colecionador";
export type PlateColor = "white" | "yellow" | "blue" | "red" | "black" | "gold" | "green" | "grey";
type PlateVariant = "mini" | "small" | "normal";

export interface LicensePlateData {
  plate: string;
  type?: PlateType;
  state: string;
}

export interface LicensePlateTheme {
  headerBg?: string;
  headerColor?: string;
  footerBg?: string;
  footerColor?: string;
  borderColor?: string;
  fontFamily?: string;
  logo?: React.ReactNode;
  flag?: React.ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
}

export interface LicensePlateProps {
  data?: LicensePlateData;
  value?: LicensePlateData;
  onChange?: (value: LicensePlateData) => void;
  plateColor?: PlateColor;
  variant?: PlateVariant;
  readOnly?: boolean;
  disabled?: boolean; // NOVO
  hidden?: boolean;
  extraInfo?: string;
  theme?: LicensePlateTheme;
  showRestrictionIcon?: boolean;
  showQRCode?: boolean;
  headerLabel?: string;
  footerStateLabel?: string;
  footerExtraLabel?: string;
  city?: string;
  fontSize?: number | string;
  onFocus?: React.FocusEventHandler<HTMLDivElement>; // NOVO
  onBlur?: React.FocusEventHandler<HTMLDivElement>;  // NOVO
  children?: React.ReactNode; // NOVO
  showLogo?: boolean; // NOVO
  showFlag?: boolean; // NOVO
}

export interface LicensePlateHandle {
  populate: (data: Partial<LicensePlateData> & { plateColor?: PlateColor; variant?: PlateVariant }) => void;
  clear: () => void;
  getValue: () => LicensePlateData;
}

const typeToColor: Record<PlateType, PlateColor> = {
  particular: "white",
  comercial: "yellow",
  oficial: "blue",
  diplomatico: "red",
  preta: "black",
  verde: "green",
  "oficial-antiga": "grey",
  colecionador: "gold",
};

const MercosulLogo: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    className="mercosul-logo"
    width={size}
    height={size}
    viewBox="0 0 32 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Mercosul"
    style={{ display: "block" }}
  >
    <g>
      <ellipse cx="16" cy="14" rx="14" ry="2" fill="#0072CE" opacity="0.15"/>
      <path d="M8 10 Q16 2 24 10" stroke="#0072CE" strokeWidth="1.5" fill="none"/>
      <circle cx="16" cy="2" r="1.5" fill="#0072CE"/>
      <circle cx="11" cy="6" r="1" fill="#0072CE"/>
      <circle cx="21" cy="6" r="1" fill="#0072CE"/>
    </g>
  </svg>
);

const MercosulFlag: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    className="mercosul-flag"
    width={size}
    height={size * 0.7}
    viewBox="0 0 18 12"
    style={{ marginRight: 4, verticalAlign: "middle" }}
    aria-label="Bandeira do Brasil"
  >
    <rect width="18" height="12" rx="1" fill="#009739"/>
    <polygon points="9,2 16,6 9,10 2,6" fill="#FFCC29"/>
    <circle cx="9" cy="6" r="2.2" fill="#3E4095"/>
    <ellipse cx="9" cy="6" rx="1.2" ry="0.5" fill="#fff" />
  </svg>
);

// Função utilitária para dividir a placa em duas linhas
// splitPlate aprimorado: aceita placas de motos, diplomáticas antigas, etc.
function splitPlate(plate: string): [string, string] {
  if (!plate) return ["", ""];
  // Placa Mercosul carro: ABC1D23
  if (/^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(plate)) {
    return [plate.slice(0, 4), plate.slice(4)];
  }
  // Placa antiga carro: ABC1234
  if (/^[A-Z]{3}[0-9]{4}$/.test(plate)) {
    return [plate.slice(0, 3), plate.slice(3)];
  }
  // Placa de moto Mercosul: ABC1D23 (mesmo padrão do carro)
  // Placa de moto antiga: ABC123 (6 caracteres)
  if (/^[A-Z]{3}[0-9]{3}$/.test(plate)) {
    return [plate.slice(0, 3), plate.slice(3)];
  }
  // Placa diplomática antiga: CC1234, CD1234, etc.
  if (/^[A-Z]{2}[0-9]{4}$/.test(plate)) {
    return [plate.slice(0, 2), plate.slice(2)];
  }
  // Placas menores (ex: motos antigas, especiais)
  if (plate.length <= 5) {
    return [plate, ""];
  }
  // Placas maiores ou formatos desconhecidos
  const mid = Math.ceil(plate.length / 2);
  return [plate.slice(0, mid), plate.slice(mid)];
}

/**
 * Componente React para exibir uma placa de veículo no padrão Mercosul ou variantes clássicas,
 * com suporte a múltiplos estilos, cores, variantes, acessibilidade e internacionalização.
 * Permite uso controlado (formulários) ou não-controlado, além de manipulação programática via ref.
 *
 * @component
 * @example
 * // Uso básico (não-controlado)
 * <LicensePlate data={{ plate: "ABC1234", state: "SP" }} />
 *
 * @example
 * // Uso controlado (formulário)
 * const [value, setValue] = useState({ plate: "DEF5678", state: "RJ", type: "comercial" });
 * <LicensePlate value={value} onChange={setValue} plateColor="yellow" variant="mini" />
 *
 * @example
 * // Uso com ref para helpers
 * const ref = useRef<LicensePlateHandle>(null);
 * <LicensePlate ref={ref} data={{ plate: "GHI9012", state: "MG" }} />
 * // Preencher programaticamente:
 * ref.current?.populate({ plate: "JKL3456", state: "RS", plateColor: "blue" });
 * // Limpar:
 * ref.current?.clear();
 * // Obter valor atual:
 * const value = ref.current?.getValue();
 *
 * @example
 * // Customização visual e internacionalização
 * <LicensePlate
 *   data={{ plate: "XYZ1234", state: "BA", type: "preta" }}
 *   plateColor="black"
 *   headerLabel="ARGENTINA"
 *   footerStateLabel="Buenos Aires"
 *   theme={{ headerBg: "#000", headerColor: "#FFD600" }}
 * />
 *
 * @param {LicensePlateProps} props
 * @param {React.Ref<LicensePlateHandle>} ref - Ref para acessar métodos helpers.
 * @returns {JSX.Element | null}
 *
 * @typedef {Object} LicensePlateData
 * @property {string} plate - Texto da placa (ex: "ABC1234").
 * @property {string} state - Sigla do estado (ex: "SP").
 * @property {PlateType=} type - Tipo da placa (ex: "particular", "preta", etc).
 *
 * @typedef {Object} LicensePlateProps
 * @property {LicensePlateData=} data - Dados da placa (modo não-controlado).
 * @property {LicensePlateData=} value - Dados da placa (modo controlado).
 * @property {(value: LicensePlateData) => void=} onChange - Callback para modo controlado.
 * @property {PlateColor=} plateColor - Cor da placa (sobrescreve o tipo).
 * @property {PlateVariant=} variant - Variante de tamanho ("mini", "small", "normal").
 * @property {boolean=} readOnly - Se true, oculta os dados reais.
 * @property {boolean=} disabled - Se true, desabilita a interação (novo).
 * @property {boolean=} hidden - Se true, não renderiza nada.
 * @property {string=} extraInfo - Informação extra para o rodapé (ex: cidade).
 * @property {LicensePlateTheme=} theme - Tema visual customizado.
 * @property {boolean=} showRestrictionIcon - Exibe ícone de restrição.
 * @property {boolean=} showQRCode - Exibe ícone de QR code.
 * @property {string=} headerLabel - Label customizada para o cabeçalho (ex: "BRASIL", "ARGENTINA").
 * @property {string=} footerStateLabel - Label customizada para o estado no rodapé.
 * @property {string=} footerExtraLabel - Label customizada para info extra no rodapé.
 * @property {string=} city - Cidade para exibir no rodapé (nova propriedade).
 *
 * @typedef {Object} LicensePlateHandle
 * @property {(data: Partial<LicensePlateData> & { plateColor?: PlateColor; variant?: PlateVariant }) => void} populate - Preenche dados da placa (atualização parcial).
 * @property {() => void} clear - Limpa os dados da placa.
 * @property {() => LicensePlateData} getValue - Retorna os dados atuais.
 *
 * @typedef {"particular"|"comercial"|"oficial"|"diplomatico"|"preta"|"verde"|"oficial-antiga"|"colecionador"} PlateType
 * @typedef {"white"|"yellow"|"blue"|"red"|"black"|"gold"|"green"|"grey"} PlateColor
 * @typedef {"mini"|"small"|"normal"} PlateVariant
 *
 * @see isMercosulPlate
 * @see isOldPlate
 * @see isValidPlate
 */
export const LicensePlate = forwardRef<LicensePlateHandle, LicensePlateProps>(
  (
    {
      data,
      value,
      onChange,
      plateColor: propPlateColor,
      variant: propVariant = "normal",
      readOnly = false,
      disabled = false, // NOVO
      hidden = false,
      extraInfo,
      theme: propsTheme,
      showRestrictionIcon,
      showQRCode,
      headerLabel: propHeaderLabel,
      footerStateLabel: propFooterStateLabel,
      footerExtraLabel: propFooterExtraLabel,
      city,
      fontSize,
      onFocus, // NOVO
      onBlur,  // NOVO
      children, // NOVO
      showLogo = true, // NOVO
      showFlag = true, // NOVO
    },
    ref
  ) => {
    // Controlled/uncontrolled logic
    const isControlled = value !== undefined && onChange !== undefined;
    const [internalData, setInternalData] = useState<LicensePlateData>(data || { plate: "", state: "", type: "particular" });
    const [internalPlateColor, setInternalPlateColor] = useState<PlateColor | undefined>(propPlateColor);
    const [internalVariant, setInternalVariant] = useState<PlateVariant>(propVariant);

    const plateData = isControlled ? value! : internalData;
    const plateColor = isControlled ? propPlateColor : internalPlateColor;
    const variant = isControlled ? propVariant : internalVariant;

    // Atualiza estado se props mudarem externamente (uncontrolled)
    React.useEffect(() => {
      if (!isControlled && data) setInternalData(data);
    }, [data, isControlled]);
    React.useEffect(() => {
      if (!isControlled) setInternalPlateColor(propPlateColor);
    }, [propPlateColor, isControlled]);
    React.useEffect(() => {
      if (!isControlled) setInternalVariant(propVariant);
    }, [propVariant, isControlled]);

    // Métodos helpers
    useImperativeHandle(ref, () => ({
      populate: (values) => {
        if (isControlled) {
          onChange?.({
            ...plateData,
            ...values,
            plate: values.plate !== undefined ? values.plate : plateData.plate,
            state: values.state !== undefined ? values.state : plateData.state,
            type: values.type !== undefined ? values.type : plateData.type,
          });
        } else {
          setInternalData((prev) => ({
            ...prev,
            ...values,
            plate: values.plate !== undefined ? values.plate : prev.plate,
            state: values.state !== undefined ? values.state : prev.state,
            type: values.type !== undefined ? values.type : prev.type,
          }));
          if (values.plateColor !== undefined) setInternalPlateColor(values.plateColor);
          if (values.variant !== undefined) setInternalVariant(values.variant);
        }
      },
      clear: () => {
        if (isControlled) {
          onChange?.({ plate: "", state: "", type: "particular" });
        } else {
          setInternalData({ plate: "", state: "", type: "particular" });
          setInternalPlateColor(undefined);
          setInternalVariant("normal");
        }
      },
      getValue: () => ({ ...plateData }),
    }));

    // Internacionalização: busca nome do país do contexto se não vier por prop
    const i18n = useContext(LicensePlateI18nContext);

    if (hidden) return null;

    const { plate, type = "particular", state } = plateData;
    const color: PlateColor = plateColor || typeToColor[type] || "white";

    const theme = propsTheme || {};
    const headerStyle = theme.headerBg || theme.headerColor
      ? { background: theme.headerBg, color: theme.headerColor }
      : undefined;
    const footerStyle = theme.footerBg || theme.footerColor
      ? { background: theme.footerBg, color: theme.footerColor }
      : undefined;
    const borderStyle = theme.borderColor
      ? { borderColor: theme.borderColor }
      : undefined;

    // Tailwind classes para cor de borda
    const colorToBorder = {
      white: "border-[#003399]",
      yellow: "border-[#FFD600]",
      blue: "border-[#0072CE]",
      red: "border-[#CC092F]",
      black: "border-[#222]",
      gold: "border-[#FFD700]",
      green: "border-[#009739]",
      grey: "border-[#888]",
    };

    // Tailwind classes para tamanho
    const variantToSize = {
      mini: "w-[120px] h-[30px] text-[12px]",
      small: "w-[220px] h-[50px] text-[18px]",
      normal: "w-[340px] h-[80px] text-[36px]",
    };

    const plateClass = clsx(
      "flex flex-col overflow-hidden relative transition-all font-bold shadow-md rounded-lg",
      "font-[PlacaBrasil,FE-Font,Arial_Black,Arial,sans-serif]",
      colorToBorder[color],
      variantToSize[variant],
      (readOnly || disabled) && "opacity-70 grayscale-[0.2] select-none pointer-events-none",
      disabled && "cursor-not-allowed"
    );

    // Header/Footer styles
    const headerClass = clsx(
      "flex items-center h-[22px] px-2 text-[13px] justify-between",
      !theme.headerBg && "bg-[#003399]",
      !theme.headerColor && "text-white"
    );

    const footerClass = clsx(
      "flex items-center h-[18px] px-2 text-[12px] justify-end",
      !theme.footerBg && "bg-[#e5e5e5]",
      !theme.footerColor && "text-[#003399]"
    );

    const RestrictionIcon = () => (
      <span
        style={{
          position: "absolute",
          left: 6,
          bottom: 6,
          fontSize: 16,
          color: "#CC092F",
          background: "#fff",
          borderRadius: "50%",
          padding: "2px 4px",
          boxShadow: "0 1px 2px #0002",
        }}
        title="Restrição"
        aria-label="Placa com restrição"
        role="img"
        tabIndex={0}
      >
        🚫
      </span>
    );

    const QRCodeIcon = () => (
      <span
        style={{
          position: "absolute",
          right: 6,
          bottom: 6,
          fontSize: 16,
          color: "#222",
          background: "#fff",
          borderRadius: 3,
          padding: "2px 4px",
          boxShadow: "0 1px 2px #0002",
        }}
        title="QR Code"
        aria-label="QR Code da placa"
        role="img"
        tabIndex={0}
      >
        {/* Simulação de QR code */}
        <svg width="16" height="16" viewBox="0 0 16 16" aria-label="QR Code" focusable="false">
          <rect x="1" y="1" width="4" height="4" fill="#222"/>
          <rect x="11" y="1" width="4" height="4" fill="#222"/>
          <rect x="1" y="11" width="4" height="4" fill="#222"/>
          <rect x="6" y="6" width="2" height="2" fill="#222"/>
          <rect x="9" y="9" width="1" height="1" fill="#222"/>
        </svg>
      </span>
    );

    const headerLabel = propHeaderLabel || i18n.country || "BRASIL";

    // Rodapé: cidade tem prioridade, depois extraInfo/footerExtraLabel
    const footerStateLabel = propFooterStateLabel || state;
    // Se city for passado, exibe cidade, senão extraInfo/footerExtraLabel
    const footerExtraLabel = city || propFooterExtraLabel || extraInfo;

    // Custom properties para fontSize e borderColor
    const customStyle: React.CSSProperties = {
      ...borderStyle,
      ...(fontSize
        ? { "--plate-font-size": typeof fontSize === "number" ? `${fontSize}px` : fontSize } as React.CSSProperties
        : {}),
    };

    return (
      <div
        className={plateClass}
        style={customStyle}
        role="region"
        aria-label={`Placa de veículo ${headerLabel}`}
        tabIndex={disabled ? -1 : 0}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {children}
        <div
          className={headerClass}
          style={headerStyle}
          role="banner"
          aria-label={`Cabeçalho da placa: ${headerLabel}`}
        >
          <span className="flex items-center gap-1">
            {showFlag && <MercosulFlag size={18} aria-hidden="true" />}
            <span className="font-bold tracking-widest" aria-label={headerLabel}>
              {headerLabel}
            </span>
          </span>
          {showLogo && <MercosulLogo size={32} aria-hidden="true" />}
        </div>
        {showRestrictionIcon && <RestrictionIcon />}
        {showQRCode && <QRCodeIcon />}
        <div
          className="flex-1 flex items-center justify-center"
          role="main"
          aria-label={`Número da placa: ${readOnly || disabled ? "oculto" : plate}`}
        >
          {variant === "mini" || variant === "small" ? (
            <kbd className="flex flex-col items-center justify-center tracking-widest w-full h-full" aria-label={`Número da placa: ${readOnly || disabled ? "oculto" : plate}`}>
              <div className={variant === "mini" ? "text-[12px] leading-tight" : "text-[18px] leading-tight"}>
                {readOnly || disabled ? "•••" : splitPlate(plate)[0]}
              </div>
              <div className={variant === "mini" ? "text-[12px] leading-tight" : "text-[18px] leading-tight"}>
                {readOnly || disabled ? "••••" : splitPlate(plate)[1]}
              </div>
            </kbd>
          ) : (
            <output
              className="text-[36px] tracking-[6px] font-bold font-[PlacaBrasil,FE-Font,Arial_Black,Arial,sans-serif] drop-shadow-sm"
              aria-label={`Número da placa: ${readOnly || disabled ? "oculto" : plate}`}
              tabIndex={0}
            >
              {readOnly || disabled ? "•••••••" : plate}
            </output>
          )}
        </div>
        <div
          className={footerClass}
          style={footerStyle}
          role="contentinfo"
          aria-label={`Rodapé da placa: ${footerStateLabel}${footerExtraLabel ? `, ${footerExtraLabel}` : ""}`}
        >
          <span className="font-bold tracking-wide" aria-label={footerStateLabel}>
            {readOnly || disabled ? "••" : footerStateLabel}
          </span>
          {footerExtraLabel && (
            <span className="ml-2 tracking-wide" aria-label={footerExtraLabel}>
              {readOnly || disabled ? "••••" : footerExtraLabel}
            </span>
          )}
        </div>
      </div>
    );
  }
);

LicensePlate.displayName = "LicensePlate";

// Funções de validação de formato de placa
export function isMercosulPlate(plate: string): boolean {
  // Ex: BRA1A23, BRA1B23, BRA2C34
  return /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(plate);
}
export function isOldPlate(plate: string): boolean {
  // Ex: ABC1234
  return /^[A-Z]{3}[0-9]{4}$/.test(plate);
}
export function isValidPlate(plate: string): boolean {
  return isMercosulPlate(plate) || isOldPlate(plate);
}

/**
 * Remove caracteres inválidos e deixa apenas letras e números maiúsculos.
 */
export function sanitizePlate(input: string): string {
  return input.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

/**
 * Formata a placa para exibição (ex: ABC1D23 → ABC-1D23, ABC1234 → ABC-1234).
 */
export function formatPlate(plate: string): string {
  if (isMercosulPlate(plate)) {
    return `${plate.slice(0, 3)}-${plate.slice(3, 4)}${plate.slice(4, 5)}${plate.slice(5)}`;
  }
  if (isOldPlate(plate)) {
    return `${plate.slice(0, 3)}-${plate.slice(3)}`;
  }
  return plate;
}


/**
 * Componente de input mascarado para placa, com preview integrado.
 */
export interface LicensePlateInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  preview?: boolean;
  plateProps?: Partial<LicensePlateProps>;
  style?: React.CSSProperties;
  className?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>; // NOVO
  onBlur?: React.FocusEventHandler<HTMLInputElement>;  // NOVO
  children?: React.ReactNode; // NOVO
}

export const LicensePlateInput: React.FC<LicensePlateInputProps> = ({
  value,
  onChange,
  placeholder = "AAA1234",
  disabled,
  readOnly,
  preview = true,
  plateProps = {},
  style,
  className,
  onFocus, // NOVO
  onBlur,  // NOVO
  children, // NOVO
}) => {
  const [internalValue, setInternalValue] = useState<string>(value || "");

  // Controlled/uncontrolled
  const plateValue = value !== undefined ? value : internalValue;

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    let val = sanitizePlate(e.target.value);
    // Limita a 7 caracteres (placa padrão)
    if (val.length > 7) val = val.slice(0, 7);
    if (onChange) onChange(val);
    else setInternalValue(val);
  }

  return (
    <div className={className} style={{ display: "flex", flexDirection: "column", gap: 8, ...style }}>
      {/* Children overlay */}
      {children}
      <input
        type="text"
        inputMode="text"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        maxLength={7}
        value={plateValue}
        onChange={handleInput}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        style={{
          fontFamily: "'PlacaBrasil','FE-Font','Arial Black',Arial,sans-serif",
          fontSize: 20,
          letterSpacing: 4,
          textTransform: "uppercase",
          padding: "6px 12px",
          border: "2px solid #003399",
          borderRadius: 6,
          outline: "none",
          width: 160,
          opacity: disabled ? 0.5 : 1, // Visual feedback para disabled
          background: disabled ? "#f5f5f5" : undefined,
          cursor: disabled ? "not-allowed" : undefined,
        }}
        aria-label="Placa do veículo"
        aria-disabled={disabled || undefined}
        aria-readonly={readOnly || undefined}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {preview && (
        <LicensePlate
          data={{
            plate: plateValue,
            state: plateProps?.data?.state || "",
            type: plateProps?.data?.type || "particular",
          }}
          readOnly={readOnly}
          disabled={disabled}
          {...plateProps}
        />
      )}
    </div>
  );
};